import { TENANT_HEADER } from "@/constants";
import { notifySessionExpired } from "@/shared/auth/authSession";
import { ensureValidBearerToken } from "@/shared/auth/ensureValidBearerToken";
import {
  AuthTokenRealmMismatchError,
  jwtMatchesUserPool,
} from "@/shared/auth/jwtClaims";
import type { AuthRealm } from "@/shared/auth/authRealm";
import { getConfiguredUserPoolId } from "@/shared/auth/cognitoRuntimeConfig";
import { env } from "@/shared/config/env";

/**
 * FastAPI `redirect_slashes` can 307 collection URLs to a trailing-slash path.
 * QA currently emits `Location: http://...` on that redirect, which browsers block
 * from HTTPS pages even when `VITE_API_BASE_URL` uses https.
 */


export function buildApiUrl(path: string): string {
  const base = env.apiBaseUrl.replace(/\/$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}

type PerformApiRequestOptions = {
  authRealm: AuthRealm;
  /** When set, sends the tenant header. Omit for admin platform APIs. */
  tenantHeader?: string;
};

/**
 * Shared Olive API `fetch` wrapper with realm-scoped Bearer tokens.
 * Tenant-scoped calls must pass `tenantHeader`; admin platform calls must omit it.
 * Validates the bearer token against the configured Cognito user pool when known.
 */
export async function performApiRequest(
  path: string,
  init: RequestInit = {},
  options: PerformApiRequestOptions,
): Promise<Response> {
  if (!env.apiBaseUrl) {
    throw new Error("Missing VITE_API_BASE_URL.");
  }

  const headers = new Headers(init.headers);

  if (options.tenantHeader !== undefined) {
    headers.set(TENANT_HEADER, options.tenantHeader);
  }

  const token = await ensureValidBearerToken(options.authRealm);
  const configuredUserPoolId = getConfiguredUserPoolId();
  if (token) {
    if (
      configuredUserPoolId &&
      !jwtMatchesUserPool(token, configuredUserPoolId)
    ) {
      notifySessionExpired();
      throw new AuthTokenRealmMismatchError();
    }
    headers.set("Authorization", `Bearer ${token}`);
  }

  let response = await fetch(buildApiUrl(path), { ...init, headers });
  if (response.status === 401) {
    if (token) {
      const refreshedToken = await ensureValidBearerToken(options.authRealm, {
        forceRefresh: true,
      });
      if (refreshedToken) {
        if (
          configuredUserPoolId &&
          !jwtMatchesUserPool(refreshedToken, configuredUserPoolId)
        ) {
          notifySessionExpired();
          throw new AuthTokenRealmMismatchError();
        }
        headers.set("Authorization", `Bearer ${refreshedToken}`);
        response = await fetch(buildApiUrl(path), { ...init, headers });
      }
    }
    if (response.status === 401) {
      notifySessionExpired();
    }
  }
  return response;
}
